import { DEFAULT_RATIO } from '@/consts/cvc';
import {
  CheckboxParameter,
  DEFAULT_MODEL,
  ModelType,
  STORAGE_KEY,
  SliderParameter,
  modelParamMap,
} from '@/pages/cvc/ModelControl/config';
import { INITIAL_MODEL_CATALOG, ModelCatalogState } from '@/util/modelCatalog';
import { atom, selector } from 'recoil';

// Model categorization constants.
// NFR-019: categories are derived only from generic patterns (the explicit
// base list plus version-prefix matching). Never enumerate customer or
// fine-tune model names here — this file ships in the production bundle.
const BASE_MODELS = ['nansy-v2.8', 'nansy-v2.7', 'nansy-v2.5', 'nansy-v2'];

const FINETUNE_PATTERNS = [
  { prefix: 'nansy-v2.8-', priority: 0 },
  { prefix: 'nansy-v2.7-', priority: 1 },
  { prefix: 'nansy-v2.5-', priority: 2 },
  { prefix: 'nansy-v2-', priority: 3 },
];

// 'specialized' is produced by no generic pattern today; it is kept as an
// extension point (Select renders it under the neutral "Custom Models" title).
type ModelCategory = 'base' | 'finetuned' | 'specialized' | 'other';

interface ModelCategorization {
  category: ModelCategory;
  priority: number;
  subPriority: string;
}

// Centralized model categorization logic
export const categorizeModel = (model: string): ModelCategorization => {
  // Base models (highest priority), in explicit BASE_MODELS order
  if (BASE_MODELS.includes(model)) {
    return {
      category: 'base',
      priority: BASE_MODELS.indexOf(model),
      subPriority: model,
    };
  }

  // Fine-tuned models: version-prefixed variants of a base model
  for (const pattern of FINETUNE_PATTERNS) {
    if (model.startsWith(pattern.prefix)) {
      return {
        category: 'finetuned',
        priority: pattern.priority,
        subPriority: model,
      };
    }
  }

  // Other models (at the bottom)
  return { category: 'other', priority: 999, subPriority: model };
};

/**
 * Runtime model catalog, populated once at boot by `useModelCatalog`.
 * Replaces the removed identity-provider permission list as the source of
 * available models.
 */
export const modelCatalogModel = atom<ModelCatalogState>({
  key: 'cvc/modelCatalog',
  default: INITIAL_MODEL_CATALOG,
});

export const isModelCatalogLoadingSelector = selector<boolean>({
  key: 'cvc/isModelCatalogLoading',
  get: ({ get }) => get(modelCatalogModel).status === 'loading',
});

const CATEGORY_ORDER: ModelCategory[] = [
  'base',
  'finetuned',
  'specialized',
  'other',
];

const sortByCategory = (models: ModelType[]) =>
  models
    .map((model) => ({ model, ...categorizeModel(model) }))
    .sort((a, b) => {
      const categoryDelta =
        CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
      if (categoryDelta !== 0) return categoryDelta;

      if (a.priority !== b.priority) return a.priority - b.priority;

      return (a.subPriority || '').localeCompare(b.subPriority || '');
    });

export const enabledModelSelector = selector<ModelType[]>({
  key: 'enabledModelSelector',
  get: ({ get }) =>
    sortByCategory(get(modelCatalogModel).models).map((item) => item.model),
});

// New selector for model categorization information with separation markers
export const modelCategorizedSelector = selector<
  Array<{ model: ModelType; category: string; isFirstInCategory: boolean }>
>({
  key: 'modelCategorizedSelector',
  get: ({ get }) => {
    const sorted = sortByCategory(get(modelCatalogModel).models);

    // Add isFirstInCategory flag -- drives the dropdown section headers
    return sorted.map((item, index) => ({
      model: item.model,
      category: item.category,
      isFirstInCategory:
        index === 0 || sorted[index - 1].category !== item.category,
    }));
  },
});

export const cvcTypeModel = atom<ModelType>({
  key: 'cvc/model',
  default: localStorage.getItem(STORAGE_KEY) as ModelType,
});

export const selectedCvcTypeSelector = selector<ModelType>({
  key: 'cvc/selectedModel',
  get: ({ get }) => {
    const cvcType = get(cvcTypeModel);
    const { status } = get(modelCatalogModel);

    // Do not churn the selection before we know what the catalog offers.
    if (status === 'loading') return cvcType ?? DEFAULT_MODEL;

    const models = get(enabledModelSelector);
    if (cvcType && models.includes(cvcType)) return cvcType;

    // A stored selection (localStorage) can outlive its model. Prefer the
    // default, then whatever the catalog does offer, before giving up.
    if (models.includes(DEFAULT_MODEL as ModelType)) {
      return DEFAULT_MODEL as ModelType;
    }
    return models[0] ?? (DEFAULT_MODEL as ModelType);
  },
  set: ({ set }, newValue) => {
    localStorage.setItem(STORAGE_KEY, newValue as string);
    set(cvcTypeModel, newValue as ModelType);
  },
});

export const isInterpolationSelector = selector<boolean>({
  key: 'cvc/isInterpolation',
  get: ({ get }) => {
    const cvcType = get(selectedCvcTypeSelector);
    return cvcType.endsWith('interpolation');
  },
});

export const interpolationRatioModel = atom<number>({
  key: 'cvc/interpolationRatio',
  default: DEFAULT_RATIO,
});

export const parameterSelector = selector<
  (CheckboxParameter | SliderParameter)[]
>({
  key: 'cvc/parameter',
  get: ({ get }) => {
    const cvcType = get(selectedCvcTypeSelector);
    // return exist model param
    if (modelParamMap[cvcType]) return modelParamMap[cvcType];
    // v2.5 or v2.7 use v2.5 params
    if (cvcType.startsWith('nansy-v2.5') || cvcType.startsWith('nansy-v2.7'))
      return modelParamMap['nansy-v2.5'];
    // v2.8 use v2.8 params
    if (cvcType.startsWith('nansy-v2.8')) return modelParamMap['nansy-v2.8'];
    // A catalog may list models this bundle has no parameter definition for;
    // fall back to the v2 set, and to an empty list if even that is missing.
    return modelParamMap['nansy-v2'] ?? [];
  },
});

export const parametersValueModel = atom<{ [key: string]: number | boolean }>({
  key: 'cvc/parameterValue',
  default: {},
});

export const mssParameterValueModel = atom<{ [key: string]: number }>({
  key: 'cvc/mssParameterValue',
  default: {},
});

export const parametersValueSelector = selector<{
  [key: string]: number | boolean;
}>({
  key: 'cvc/parameterValueSelector',
  get: ({ get }) => {
    const parameters = get(parameterSelector);
    const values = get(parametersValueModel);

    // Get all parameters with their values. `parameters` can be empty for a
    // catalog model with no parameter definition -- never index into undefined.
    const allParams = (parameters ?? []).reduce((acc, parameter) => {
      acc[parameter.name] = values[parameter.name] ?? parameter.defaultValue;
      return acc;
    }, {} as { [key: string]: number | boolean });

    // Filter out parameters based on gender_age_mode
    const isAbsoluteMode = allParams['gender_age_mode'] === true;

    if (allParams['gender_age_mode'] !== undefined) {
      // If absolute mode is enabled, remove relative parameters
      if (isAbsoluteMode) {
        delete allParams['age_shift'];
        delete allParams['gender_shift'];
      } else {
        // If relative mode is enabled, remove absolute parameters
        delete allParams['age_trg'];
        delete allParams['gender_trg'];
      }
    }

    return allParams;
  },
});

export const isSourceOnlySelector = selector<boolean>({
  key: 'cvc/isSourceOnly',
  get: ({ get }) => {
    const cvcType = get(selectedCvcTypeSelector);
    return cvcType === 'speaker-control';
  },
});
